// src/pages/SelfPublishingService.js

import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import VideoHero from '../components/shared/VideoHero';

// Animasyonları tetiklemek için özel bir hook (component içinde tanımlı)
const useFadeInEffect = () => {
  const animatedElements = useRef([]);

  useEffect(() => {
    // ESLint hatasını gidermek için referansın o anki değerini bir değişkene atıyoruz.
    const elementsToObserve = animatedElements.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    if (elementsToObserve) {
        elementsToObserve.forEach((el) => {
          if (el) observer.observe(el);
        });
    }

    // Temizleme fonksiyonunda artık bu güvenli değişkeni kullanıyoruz.
    return () => {
      if (elementsToObserve) {
        elementsToObserve.forEach((el) => {
          if (el) observer.unobserve(el);
        });
      }
    };
  }, []);

  const addToRefs = (el) => {
    if (el && !animatedElements.current.includes(el)) {
      animatedElements.current.push(el);
    }
  };

  return addToRefs;
};


const SelfPublishingService = () => {
  const ctaLink = "/contact";
  const addToRefs = useFadeInEffect();

  // Galeri için kullanılacak resimler
  const galleryImages = [
    'services6.jpg',
    'services7.jpg',
    'services9.jpg'
  ];

  return (
    <>
      <Helmet>
        <title>Diversity, Equity & Inclusion | Little Hibba</title>
        <meta name="description" content="Stories Rooted in Culture, Faith, and Belonging. Discover our commitment to diversity, equity, and inclusion at Little Hibba." />
      </Helmet>

      <div id="dei-hero-wrapper">
        <VideoHero 
          title="Diversity, Equity & Inclusion" 
          tagline="Stories Rooted in Culture, Faith, and Belonging."
        />
      </div>

      <main className="dei-page-container page-transition">
        {/* Section 1: Giriş ve Misyon (VİDEO SAĞDA) */}
        <section ref={addToRefs} className="dei-section fade-in-section">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-7">
                <h2 className="dei-section-title">Let's Work Together</h2>
                <p>At Little Hibba, we believe stories have the power to shape hearts and change futures. Our mission is to create books and projects that reflect the diversity of the Muslim community and the wider world, nurturing cultural and religious sensitivity while celebrating shared values of kindness, compassion, and respect.</p>
                <p>We’re passionate about ensuring every child — especially those from Muslim and multicultural backgrounds — can see themselves represented with dignity, accuracy, and pride.</p>
              </div>
              <div className="col-lg-5 d-flex justify-content-center">
                <div className="dei-reel-frame">
                  <video className="dei-inline-video" autoPlay loop muted playsInline>
                    <source src="/images/services/servicesvid1.mp4" type="video/mp4" /> 
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: DEI Sözümüz (VİDEO SOLDA) - MOBİL SIRALAMA GÜNCELLENDİ */}
        <section ref={addToRefs} className="dei-section bg-light-custom fade-in-section">
          <div className="container">
            <div className="row align-items-center">
              {/* MOBİL İÇİN 'order-2' EKLENDİ - Bu sütun mobilde 2. sırada olacak */}
              <div className="col-lg-5 d-flex justify-content-center order-2 order-lg-1">
                <div className="dei-reel-frame">
                  <video className="dei-inline-video" autoPlay loop muted playsInline>
                    <source src="/images/services/servicesvid2.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              {/* MOBİL İÇİN 'order-1' EKLENDİ - Bu sütun mobilde 1. sırada olacak */}
              <div className="col-lg-7 order-1 order-lg-2">
                <h2 className="dei-section-title">Our DEI & Cultural Sensitivity Promise</h2>
                <p>At Little Hibba, diversity, equity, and inclusion are not just ideas — they’re part of our foundation. We’re guided by principles of fairness, understanding, and faith, ensuring our books and partnerships always reflect integrity and respect.</p>
                <ul className="dei-icon-list">
                  <li><i className="bi bi-check-circle-fill"></i> Authentic Representation</li>
                  <li><i className="bi bi-check-circle-fill"></i> Cultural & Religious Sensitivity</li>
                  <li><i className="bi bi-check-circle-fill"></i> Equity in Opportunity</li>
                  <li><i className="bi bi-check-circle-fill"></i> Inclusive Narratives</li>
                  <li><i className="bi bi-check-circle-fill"></i> Community Impact</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
        
        {/* Section 3: DEI Projeleri ve Programları */}
        <section ref={addToRefs} className="dei-section fade-in-section">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="dei-section-title">Our DEI Projects & Programmes</h2>
            </div>
            <div className="row justify-content-center">
              <div className="col-lg-4 col-md-6 mb-4">
                <div className="dei-project-card h-100">
                  <img src="/images/services/services14.jpg" className="card-img-top" alt="Stories of Faith & Friendship" />
                  <div className="card-body">
                    <h3 className="card-title">🌙 Stories of Faith & Friendship</h3>
                    <p className="card-text">A beautifully curated series exploring the everyday lives of Muslim children, highlighting shared values like honesty, patience, gratitude, and compassion.</p>
                  </div>
                </div>
              </div>
              <div className="col-lg-4 col-md-6 mb-4">
                <div className="dei-project-card h-100">
                  <img src="/images/services/services12.jpg" className="card-img-top" alt="Voices of Belonging" />
                  <div className="card-body">
                    <h3 className="card-title">🕌 Voices of Belonging</h3>
                    <p className="card-text">An empowering platform for families to share stories of identity. We help new writers turn lived experiences into stories that resonate across generations.</p>
                  </div>
                </div>
              </div>
              <div className="col-lg-4 col-md-6 mb-4">
                <div className="dei-project-card h-100">
                  <img src="/images/services/services11.jpg" className="card-img-top" alt="Storytime for All" />
                  <div className="card-body">
                    <h3 className="card-title">🕊️ Storytime for All</h3>
                    <p className="card-text">We collaborate with mosques, schools, and libraries to host inclusive storytime events where children can share in the joy of reading and discovery.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Galeri Bölümü */}
        <section ref={addToRefs} className="dei-section bg-light-custom fade-in-section">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="dei-section-title">Moments from Our Community</h2>
            </div>
            <div className="row g-4">
              {galleryImages.map((image, index) => (
                <div key={index} className="col-lg-4 col-md-6">
                  <div className="dei-gallery-item">
                    <img src={`/images/services/${image}`} alt={`Community moment ${index + 1}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Section 5: Call to Action (CTA) */}
        <section ref={addToRefs} className="dei-cta-section text-center fade-in-section">
            <div className="container">
                <h2 className="dei-cta-title">Join Our Mission</h2>
                <p className="dei-cta-text">Help us bring more inclusive and authentic stories to the world. Let's create something beautiful together.</p>
                <a href={ctaLink} className="dei-cta-button" target="_blank" rel="noopener noreferrer">
                    Get in Touch
                </a>
            </div>
        </section>
      </main>
    </>
  );
};

export default SelfPublishingService;